import { AppSettings } from "@/AppSettings";
import IsLoading from "@/components/layouts/IsLoading";
import SetLinks from "@/components/layouts/SetLinks";
import Show from "@/components/layouts/Show";
import CoordinatingGroupsContext from "@/contexts/data/CoordinatingGroupsContext";
import InstitutionsContext from "@/contexts/data/InstitutionsContext";
import MasterGroupsContext from "@/contexts/data/MasterGroupsContext";
import TrialContext from "@/contexts/data/TrialContext";
import { ConfigurationInstitutionCreateLink, ConfigurationTrialCreateLink, NotesAndQueriesInstitutionCreateLink, NotesAndQueriesTrialCreateLink } from "@/contexts/layouts/LinkContext";
import useConfigurationTrial from "@/hooks/data/useConfigurationTrial";
import useTrialNotesAndQueries from "@/hooks/data/useTrialNotesAndQueries";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Divider, List, ListItemButton, ListItemIcon, ListItemText, Paper, Typography, styled } from "@mui/material";
import { FC, useContext } from "react";
import { Link } from "react-router-dom";

export const StatusListItemButton = styled(ListItemButton)(({ theme }) => ({
    padding: theme.spacing(0, 2),
    minHeight: theme.spacing(6)
}));

export const StatusListItemText = styled(ListItemText)(({ theme }) => ({
    margin: theme.spacing(0.5, 0)
}));

export const StatusListItemIcon = styled(ListItemIcon)(({ theme }) => ({
    minWidth: theme.spacing(6)
}));

const Configuration: FC = () => {
    const { data: trial } = useContext(TrialContext);
    const { data: masterGroups } = useContext(MasterGroupsContext);
    const { data: coordinatingGroups } = useContext(CoordinatingGroupsContext);
    const { data: institutions } = useContext(InstitutionsContext);
    const { data: configurationTrial, loading: configurationTrialLoading, validating: configurationTrialValidating } = useConfigurationTrial(trial?.id ? { trialId: trial?.id } : undefined, { revalidateOnFocus: false });


    const loading = configurationTrialLoading;
    const validating = configurationTrialValidating;

    return (
        <>
            <SetLinks 
                createTrialLink={ConfigurationTrialCreateLink} 
                createInstitutionLink={ConfigurationInstitutionCreateLink} 
                institutionLinkEnabled={false} 
                participantLinkEnabled={false} 
            />
            <IsLoading name="configuration-page" loading={loading} validating={validating} />
            <Show show={!loading}>
                <Typography variant="h1" gutterBottom>
                    Configuration
                </Typography>
                <Paper>
                    <List disablePadding>
                        <StatusListItemButton  
                            //@ts-ignore-next-line
                            component={Link} 
                            to={`/${trial?.oid}/configuration/trial`}
                        >
                            <StatusListItemIcon>
                                <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.trial} />
                            </StatusListItemIcon>
                            <StatusListItemText 
                                primary="Trial"
                            />
                        </StatusListItemButton>
                        {
                            masterGroups && masterGroups.length > 0 && (
                                <>
                                    <Divider />
                                    <StatusListItemButton 
                                        //@ts-ignore-next-line
                                        component={Link} 
                                        to={`/${trial?.oid}/configuration/master-groups`}
                                    >
                                        <StatusListItemIcon>
                                            <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.masterGroup} />
                                        </StatusListItemIcon>
                                        <StatusListItemText primary="Master Groups" />
                                    </StatusListItemButton>
                                </>
                            )
                        }
                        {
                            coordinatingGroups && coordinatingGroups.length > 0 && (
                                <>
                                    <Divider />
                                    <StatusListItemButton  
                                        //@ts-ignore-next-line
                                        component={Link} 
                                        to={`/${trial?.oid}/configuration/coordinating-groups`}
                                    >
                                        <StatusListItemIcon>
                                            <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.coordinatingGroup} />
                                        </StatusListItemIcon>
                                        <StatusListItemText primary="Coordinating Groups" />
                                    </StatusListItemButton>
                                </>
                            )
                        }
                        {
                            institutions && institutions.length > 0 && (
                                <>
                                    <Divider />
                                    <StatusListItemButton 
                                        //@ts-ignore-next-line
                                        component={Link} 
                                        to={`/${trial?.oid}/configuration/institutions`}
                                    >
                                        <StatusListItemIcon>
                                            <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.institution} />
                                        </StatusListItemIcon>
                                        <StatusListItemText primary="Institutions" />
                                    </StatusListItemButton>
                                </>
                            )
                        }
                        <Divider />
                        <StatusListItemButton 
                            //@ts-ignore-next-line
                            component={Link} 
                            to={`/${trial?.oid}/configuration/participants`}
                        >
                            <StatusListItemIcon>
                                <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.participant} />
                            </StatusListItemIcon>
                            <StatusListItemText primary="Participants" />
                        </StatusListItemButton>
                        <Divider />
                        <StatusListItemButton 
                            //@ts-ignore-next-line
                            component={Link} 
                            to={`/${trial?.oid}/configuration/source-data`}
                        >
                            <StatusListItemIcon>
                                <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.sourceData} />
                            </StatusListItemIcon>
                            <StatusListItemText primary="Source Data" />
                        </StatusListItemButton>
                        <Divider />
                        <StatusListItemButton 
                            //@ts-ignore-next-line
                            component={Link} 
                            to={`/${trial?.oid}/configuration/utilities`}
                        >
                            <StatusListItemIcon>
                                <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.utilities} />
                            </StatusListItemIcon>
                            <StatusListItemText primary="Utilities" />
                        </StatusListItemButton>
                    </List>
                </Paper>
            </Show>
        </>
    )
};

export default Configuration;